import {
  CCPaymentFields,
  CreateEdiPostType,
  EpayCustomerTransactionAction,
  EpayModifyTransaction,
  Insurance,
  PatientInvoice,
  PatientPayment,
  PatientTransaction,
} from '@chiroup/core';
import { ChiroUpAPI } from '@chiroup/client-core';
import { PaymentFilters } from '../components/patients/detail/billing/hooks/usePayments';
import { TransactionFilters } from '../components/patients/detail/billing/hooks/useTransactions';

const patientBillingService = () => {
  const listPatientPayment = async (
    clinicId?: number,
    params?: PaymentFilters & {
      skip?: number;
      limit?: number;
      patient?: string;
      credits?: boolean;
    },
  ) => {
    return ChiroUpAPI.get('api', `/transactions/${clinicId}/payment`, {
      queryParams: params,
    });
  };

  const getPatientPayment = async ({
    clinicId,
    paymentId,
  }: {
    clinicId: number;
    paymentId: string;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/payment/${paymentId}`,
      {},
    );
  };

  const savePatientPayment = async ({
    clinicId,
    payment,
  }: {
    clinicId: number;
    payment: Partial<PatientPayment>;
  }) => {
    if (payment.id) {
      return ChiroUpAPI.put(
        'api',
        `/transactions/${clinicId}/payment/${payment.id}`,
        { body: payment },
      );
    }
    return ChiroUpAPI.post('api', `/transactions/${clinicId}/payment`, {
      body: payment,
    });
  };

  const createPatientInsurance = async (
    body: Partial<Insurance>,
    clinicId?: number,
    patientId?: string,
  ) => {
    return ChiroUpAPI.post(
      'api',
      `/patients/${clinicId}/patients/${patientId}/insurance`,
      {
        body,
      },
    );
  };

  const listPatientInsurance = async (
    clinicId?: number,
    patientId?: string,
  ) => {
    return ChiroUpAPI.get(
      'api',
      `/patients/${clinicId}/patients/${patientId}/insurance`,
      {},
    );
  };

  const listPatientTransaction = async (
    clinicId?: number,
    params?: TransactionFilters & {
      skip?: number;
      limit?: number;
      patient?: string;
    },
  ) => {
    return ChiroUpAPI.get('api', `/transactions/${clinicId}/transaction`, {
      queryParams: params,
    });
  };

  const listPatientTransactionActivity = async (
    clinicId?: number,
    billingKey?: string,
  ) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/transaction/${billingKey}/activity`,
      {},
    );
  };

  const getPatientTransaction = async (
    clinicId?: number,
    billingKey?: string,
  ) => {
    if (!billingKey || !clinicId || billingKey === 'new') {
      return Promise.reject({ statusCode: 404, message: 'Not found.' });
    }

    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/transaction/${billingKey}`,
      {},
    );
  };

  const savePatientTransaction = async (args: {
    clinicId?: number;
    transaction?: PatientTransaction;
  }) => {
    const { clinicId, transaction } = args;
    const billingKey = transaction?.billingKey;
    /**
     * No REST if they are wicked (and don't pass parameters).
     */
    if (!billingKey || !clinicId || billingKey === 'new') return [];
    if (transaction?.id && transaction.id > 0) {
      return ChiroUpAPI.put(
        'api',
        `/transactions/${clinicId}/transaction/${billingKey}`,
        { body: transaction },
      );
    }
    return ChiroUpAPI.post('api', `/transactions/${clinicId}/transaction`, {
      body: transaction,
    });
  };

  const createTransactionActivity = async ({
    billingKey,
    clinicId,
  }: {
    billingKey: string;
    clinicId: number;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/transaction/${billingKey}/activity`,
      {},
    );
  };

  const createTransactionActivities = async ({
    billingKeys,
    clinicId,
    type,
  }: {
    billingKeys: string[];
    clinicId: number;
    type?: string;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/transaction/activities`,
      {
        body: { billingKeys, type },
      },
    );
  };

  const getPatientInsurance = async (
    id: string,
    clinicId?: number,
    patientId?: string,
  ) => {
    return ChiroUpAPI.get(
      'api',
      `/patients/${clinicId}/patients/${patientId}/insurance/${id}`,
      {},
    );
  };

  const updatePatientInsurance = async (
    id: string,
    values: Partial<Insurance>,
    clinicId?: number,
    patientId?: string,
  ) => {
    return ChiroUpAPI.put(
      'api',
      `/patients/${clinicId}/patients/${patientId}/insurance/${id}`,
      {
        body: { ...values },
      },
    );
  };

  const deletePatientInsurance = async (
    id: string,
    clinicId?: number,
    patientId?: string,
  ) => {
    return ChiroUpAPI.del(
      'api',
      `/patients/${clinicId}/patients/${patientId}/insurance/${id}`,
      {},
    );
  };

  const getEpayPublicKey = async (clinicId: number) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/get-payment-key`,
      {},
    );
  };

  const submitEpayPayment = async ({
    values,
    patientId,
    clinicId,
    paymentKey,
    payMethodKey,
  }: {
    values: CCPaymentFields;
    patientId: string;
    clinicId?: number;
    paymentKey?: string;
    payMethodKey?: string;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/${patientId}/submit-payment`,
      {
        body: { paymentKey, payMethodKey, values },
      },
    );
  };

  const getEpayCustomer = async (patientId: string, clinicId?: number) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/${patientId}/get-customer`,
      {},
    );
  };

  const listEpayCustomerTransactions = async (
    patientId: string,
    clinicId?: number,
  ) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/${patientId}/customer-transactions`,
      {},
    );
  };

  const listEpayBatches = async (clinicId: number) => {
    return ChiroUpAPI.get('api', `/transactions/${clinicId}/batches`, {});
  };

  const getEpayBatch = async (clinicId: number, key: string) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/batches/${key}`,
      {},
    );
  };

  const closeEpayBatch = async (clinicId: number, key: string) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/batches/${key}`,
      {},
    );
  };

  const ePayTransactionAction = async (
    patientId: string,
    action: EpayCustomerTransactionAction,
    item: EpayModifyTransaction,
    clinicId?: number,
  ) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/${patientId}/customer-transaction`,
      {
        body: { item, action },
      },
    );
  };

  const deletePaymentTransaction = async (
    clinicId: number,
    billingKey: string,
  ) => {
    return ChiroUpAPI.del(
      'api',
      `/transactions/${clinicId}/payment/${billingKey}`,
      {},
    );
  };

  const refundPaymentTransaction = async (
    clinicId: number,
    billingKey: string,
  ) => {
    return ChiroUpAPI.put(
      'api',
      `/transactions/${clinicId}/payment/${billingKey}/refund`,
      {},
    );
  };

  const getEpayTerminal = async ({
    clinicId,
    deviceKey,
  }: {
    clinicId: number;
    deviceKey: string;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/terminal/${deviceKey}`,
      {},
    );
  };

  const createNewPayRequest = async ({
    amount,
    clinicId,
    patientId,
    deviceKey,
  }: {
    amount: number;
    clinicId: number;
    patientId: string;
    deviceKey: string;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/${patientId}/payment-engine-request`,
      {
        body: { amount, deviceKey },
      },
    );
  };

  const updatePayRequest = async ({
    billingKey,
    payload,
    clinicId,
    deviceKey,
  }: {
    billingKey: string;
    payload: PatientPayment & CCPaymentFields;
    clinicId: number;
    patientId: string;
    deviceKey: string;
  }) => {
    return ChiroUpAPI.put(
      'api',
      `/transactions/${clinicId}/payment/${billingKey}`,
      {
        body: { billingKey, payload, deviceKey },
      },
    );
  };

  const checkPayRequestStatus = async ({
    clinicId,
    payRequestKey,
  }: {
    clinicId: number;
    payRequestKey: string;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/payment-engine-request/${payRequestKey}`,
      {},
    );
  };

  const getInvoice = async ({
    invoiceId,
    clinicId,
  }: {
    invoiceId: number;
    clinicId: number;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/invoice/${invoiceId}`,
      {},
    );
  };

  /**
   * This does a POST, as it needs a payload with a lot of data.
   *
   * @param param0
   * @returns
   */
  const get837P = async ({
    payload,
    clinicId,
  }: {
    payload: CreateEdiPostType;
    clinicId: number;
  }) => {
    return ChiroUpAPI.post('api', `/transactions/${clinicId}/invoice/837p`, {
      body: payload,
    });
  };

  const listInvoices = async ({
    billingKey,
    patientId,
    clinicId,
  }: {
    billingKey: string;
    patientId: string;
    clinicId: number;
  }) => {
    if (!billingKey || !clinicId || !patientId) return null;
    return ChiroUpAPI.get('api', `/transactions/${clinicId}/invoice`, {
      queryParams: { billingKey, patientId },
    });
  };

  const listAllPatientInvoicesByBillingKeys = async ({
    patientId,
    clinicId,
    billingKeys,
  }: {
    patientId: string;
    clinicId: number;
    billingKeys: string[];
  }) => {
    if (!clinicId || !patientId) return null;
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/invoice/${patientId}/billing-keys`,
      {
        queryParams: { billingKeys },
      },
    );
  };

  const updatePatientInvoice = async ({
    invoiceData,
    invoiceId,
    patientId,
    clinicId,
  }: {
    invoiceData: PatientInvoice;
    invoiceId: number;
    patientId: string;
    clinicId: number;
  }) => {
    return ChiroUpAPI.put(
      'api',
      `/transactions/${clinicId}/invoice/${invoiceId}`,
      { body: { invoiceData, patientId } },
    );
  };

  const emailPatientInvoice = async ({
    invoiceId,
    clinicId,
  }: {
    invoiceId: number;
    clinicId: number;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/invoice/${invoiceId}/email`,
      {},
    );
  };

  const writeOffPatientInvoice = async ({
    invoiceId,
    clinicId,
  }: {
    invoiceId: number;
    clinicId: number;
  }) => {
    return ChiroUpAPI.post(
      'api',
      `/transactions/${clinicId}/invoice/${invoiceId}/write-off`,
      {},
    );
  };

  const generateStatement = async ({
    clinicId,
    patientId,
    startDate,
    endDate,
    type,
    payors,
  }: {
    clinicId: number;
    patientId: string;
    startDate: string;
    endDate: string;
    type: string;
    payors: string;
  }) => {
    return ChiroUpAPI.get(
      'api',
      `/transactions/${clinicId}/statement/${patientId}`,
      {
        queryParams: { startDate, endDate, type, payors },
      },
    );
  };

  return {
    checkPayRequestStatus,
    closeEpayBatch,
    createNewPayRequest,
    createPatientInsurance,
    deletePatientInsurance,
    deletePaymentTransaction,
    ePayTransactionAction,
    get837P,
    getEpayBatch,
    getEpayCustomer,
    getEpayPublicKey,
    getEpayTerminal,
    getInvoice,
    getPatientInsurance,
    getPatientPayment,
    getPatientTransaction,
    listEpayBatches,
    listEpayCustomerTransactions,
    listInvoices,
    listPatientInsurance,
    listPatientPayment,
    listPatientTransaction,
    listPatientTransactionActivity,
    refundPaymentTransaction,
    savePatientPayment,
    savePatientTransaction,
    submitEpayPayment,
    updatePatientInsurance,
    updatePatientInvoice,
    createTransactionActivity,
    updatePayRequest,
    emailPatientInvoice,
    writeOffPatientInvoice,
    generateStatement,
    createTransactionActivities,
    listAllPatientInvoicesByBillingKeys,
  };
};

export default patientBillingService();
